import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, message, Modal, notification, Row, Spin } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useHistory } from 'react-router-dom';
import ReactCodeInput from 'react-code-input';
import AuthService from '../AuthService';
import { EnrollmentContext } from '../../../providers/EnrollmentContextProvider';
import enrollmentAction from '../../../providers/EnrollmentContextProvider/enrollmentAction';
import Paragraph from '../../../components/common/Paragraph';
import Text from '../../../components/common/Text';

const EmailVerificationModal = ({
  token,
  isVisible,
  onCancel,
  isResending,
  resend,
  timeLeft,
  hasExpired,
  isResendEnabled,
}) => {
  const [isLoading, setLoading] = useState(false);
  const [isCodeWrong, setCodeWrong] = useState(false);

  const [form] = useForm();
  const history = useHistory();

  const { enrollmentForm, dispatch } = useContext(EnrollmentContext);
  const { email, rut } = enrollmentForm.contact;

  useEffect(() => {
    if (hasExpired && isVisible) {
      const key = Date.now();

      notification.error({
        key,
        message: 'Código expirado',
        description:
          'Su código de verificación ha expirado. Si desea continuar, por favor presione "Reenviar código temporal"',
        duration: 0,
        btn: (
          <Button type="primary" size="small" onClick={() => notification.close(key)}>
            Entendido
          </Button>
        ),
      });
    }
  }, [hasExpired, isVisible]);

  const handleSubmit = (values) => {
    const payload = {
      token,
      code: Number(values.code),
    };

    setLoading(true);

    AuthService.validateCode(payload)
      .then(({ data }) => {
        dispatch({ type: enrollmentAction.SET_AUTH_TOKEN, payload: data.token });
        dispatch({ type: enrollmentAction.SET_POSTULANT_EMAIL, payload: email });
        dispatch({ type: enrollmentAction.SET_POSTULANT_RUT, payload: rut });
        message.success('E-mail verificado con éxito!');
        setLoading(false);
        history.push('/enrolamiento/formulario');
      })
      .catch((error) => {
        if (error.status === 403) {
          form.resetFields();
          setCodeWrong(true);
        } else {
          message.error('Hubo un error inesperado. Por favor intente nuevamente.');
        }

        setLoading(false);
      });
  };

  const handleCancel = () => {
    if (!isLoading && !isResending) {
      form.resetFields();
      setCodeWrong(false);
      onCancel();
    }
  };

  const handleCodeChange = () => {
    setCodeWrong(false);
  };

  const handleResendClick = () => {
    form.resetFields();
    resend();
  };

  return (
    <Modal visible={isVisible} onCancel={handleCancel} footer={null} width={420}>
      <p>
        <b>Verificación de usuario</b>
      </p>
      <Paragraph align="center">
        <Text>Para continuar, ingresa el código de verificación temporal enviado a tu e-mail:</Text>
        <br />
        <b>{email}</b>
      </Paragraph>
      <Spin spinning={isLoading || isResending}>
        <Row justify="center">
          <Col span={20}>
            <Form layout="vertical" form={form} onFinish={handleSubmit} hideRequiredMark>
              {!hasExpired && (
                <>
                  <Paragraph align="center">Ingresa el código temporal</Paragraph>
                  <Row justify="center">
                    <Col>
                      <Form.Item
                        name="code"
                        rules={[{ required: true, message: 'Ingrese el código' }]}
                        validateStatus={isCodeWrong ? 'error' : undefined}
                        help={isCodeWrong ? 'Código incorrecto' : undefined}
                        onChange={handleCodeChange}
                      >
                        <ReactCodeInput
                          fields={6}
                          inputStyle={{
                            fontFamily: 'Hind',
                            MozAppearance: 'textfield',
                            borderRadius: '6px',
                            border: '1px solid',
                            margin: '4px',
                            paddingLeft: '10px',
                            paddingTop: '3px',
                            width: '36px',
                            height: '40px',
                            fontSize: '30px',
                            boxSizing: 'border-box',
                            color: 'black',
                            backgroundColor: 'white',
                            borderColor: 'lightgrey',
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item>
                    {hasExpired ? (
                      <>
                        <Paragraph align="center" type="danger" noBottomMargin>
                          Código expirado
                        </Paragraph>
                      </>
                    ) : (
                      <Button type="primary" htmlType="submit" block disabled={hasExpired}>
                        Ingresar
                      </Button>
                    )}
                  </Form.Item>
                  <Paragraph align="center" type="secondary">
                    Tiempo restante: {timeLeft}
                  </Paragraph>
                </>
              )}
              <Form.Item>
                {!hasExpired && (
                  <Paragraph align="center" noBottomMargin>
                    ¿Problemas con el código?
                  </Paragraph>
                )}
                <Button
                  loading={isLoading}
                  type={hasExpired ? 'primary' : 'link'}
                  onClick={handleResendClick}
                  block
                  disabled={!isResendEnabled}
                >
                  {hasExpired ? (
                    'Reenviar código temporal'
                  ) : (
                    <Text
                      type={isResendEnabled ? undefined : 'secondary'}
                      underline={isResendEnabled}
                    >
                      Reenviar código temporal
                    </Text>
                  )}
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};

EmailVerificationModal.propTypes = {
  token: PropTypes.string,
  isVisible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  isResending: PropTypes.bool,
  resend: PropTypes.func.isRequired,
  timeLeft: PropTypes.string.isRequired,
  hasExpired: PropTypes.bool.isRequired,
  isResendEnabled: PropTypes.bool.isRequired,
};

EmailVerificationModal.defaultProps = {
  token: undefined,
  isResending: undefined,
};

export default EmailVerificationModal;
